import React from 'react';
import { Button, Checkbox, Icon, Label, Table } from 'semantic-ui-react';
import { timestamp2string } from '../../helpers';
import { formatNumber } from './ModelMetrics';

function renderStatus(model) {
  if (!model.enabled) return <Label color='red'>停用</Label>;
  if (model.enabled_channel_count > 0) return <Label color='green'>可用</Label>;
  return <Label color='grey'>未接通路</Label>;
}

function renderTags(tags) {
  if (!tags) return <span className='model-muted'>-</span>;
  return tags
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean)
    .slice(0, 4)
    .map((tag) => (
      <Label basic size='mini' key={tag}>
        {tag}
      </Label>
    ));
}

function hasNullableValue(value) {
  return value !== '' && value !== null && value !== undefined;
}

function hasParameterConfig(model) {
  return Boolean(
    model.default_params ||
      model.default_system_prompt ||
      Number(model.default_max_tokens || 0) > 0 ||
      hasNullableValue(model.default_temperature) ||
      hasNullableValue(model.default_top_p) ||
      Number(model.default_top_k || 0) > 0 ||
      hasNullableValue(model.default_presence_penalty) ||
      hasNullableValue(model.default_frequency_penalty) ||
      model.default_stop ||
      model.default_reasoning_effort ||
      model.default_response_format ||
      model.default_extra_params
  );
}

function renderConfigLabels(model) {
  const labels = [];
  if (hasParameterConfig(model)) {
    labels.push(
      <Label key='params' basic color='teal' size='mini'>
        参数
      </Label>
    );
  }
  if (model.api_enabled) {
    labels.push(
      <Label key='api' basic color='violet' size='mini'>
        三方 API
      </Label>
    );
  }
  if (model.api_key_set) {
    labels.push(
      <Label key='key' basic color='grey' size='mini'>
        密钥
      </Label>
    );
  }
  if (model.version) {
    labels.push(
      <Label key='version' basic color='blue' size='mini'>
        v{model.version}
      </Label>
    );
  }
  if (labels.length === 0) return <span className='model-muted'>-</span>;
  return <div className='model-config-labels'>{labels}</div>;
}

const ModelTable = ({
  models,
  selectedNames,
  selectableNames,
  onSelect,
  onSelectAll,
  onEdit,
  onToggle,
  onDelete,
  onChanges,
}) => {
  const selectedSet = new Set(selectedNames);
  const allSelected =
    selectableNames.length > 0 &&
    selectableNames.every((name) => selectedSet.has(name));
  const partiallySelected =
    !allSelected && selectableNames.some((name) => selectedSet.has(name));

  return (
    <div className='model-table-wrap'>
      <Table basic='very' selectable unstackable className='model-table'>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell collapsing className='model-select-cell'>
              <Checkbox
                disabled={selectableNames.length === 0}
                checked={allSelected}
                indeterminate={partiallySelected}
                onChange={(_, { checked }) => onSelectAll(checked)}
              />
            </Table.HeaderCell>
            <Table.HeaderCell>模型</Table.HeaderCell>
            <Table.HeaderCell>状态</Table.HeaderCell>
            <Table.HeaderCell>倍率</Table.HeaderCell>
            <Table.HeaderCell>补全</Table.HeaderCell>
            <Table.HeaderCell>通路</Table.HeaderCell>
            <Table.HeaderCell>调用</Table.HeaderCell>
            <Table.HeaderCell>标签</Table.HeaderCell>
            <Table.HeaderCell>配置</Table.HeaderCell>
            <Table.HeaderCell textAlign='right'>操作</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {models.map((model) => (
            <Table.Row key={model.name}>
              <Table.Cell collapsing className='model-select-cell'>
                <Checkbox
                  checked={selectedSet.has(model.name)}
                  title='选择模型'
                  onChange={(_, { checked }) => onSelect(model.name, checked)}
                />
              </Table.Cell>
              <Table.Cell>
                <div className='model-name'>
                  <strong>{model.display_name || model.name}</strong>
                  <span>{model.name}</span>
                  <small>
                    {model.provider || '未设置供应商'} · {model.modality || 'chat'}
                  </small>
                </div>
              </Table.Cell>
              <Table.Cell>
                <div className='model-status'>
                  {renderStatus(model)}
                  {model.managed ? (
                    <Label basic color='blue'>
                      托管
                    </Label>
                  ) : (
                    <Label basic>发现</Label>
                  )}
                </div>
              </Table.Cell>
              <Table.Cell>{formatNumber(model.ratio, 4)}</Table.Cell>
              <Table.Cell>{formatNumber(model.completion_ratio, 4)}</Table.Cell>
              <Table.Cell>
                <strong>{formatNumber(model.enabled_channel_count)}</strong>
                <span className='model-muted'> / {formatNumber(model.channel_count)}</span>
              </Table.Cell>
              <Table.Cell>
                <strong>{formatNumber(model.request_count)}</strong>
                <span className='model-muted'> 次</span>
                {model.last_used_at ? (
                  <small className='model-last-used'>
                    {timestamp2string(model.last_used_at)}
                  </small>
                ) : null}
              </Table.Cell>
              <Table.Cell>{renderTags(model.tags)}</Table.Cell>
              <Table.Cell>{renderConfigLabels(model)}</Table.Cell>
              <Table.Cell textAlign='right'>
                <Button.Group size='mini'>
                  <Button icon title='编辑' onClick={() => onEdit(model)}>
                    <Icon name='edit' />
                  </Button>
                  <Button icon title='变更记录' onClick={() => onChanges(model)}>
                    <Icon name='history' />
                  </Button>
                  <Button
                    icon
                    title={model.enabled ? '停用' : '启用'}
                    onClick={() => onToggle(model)}
                  >
                    <Icon name={model.enabled ? 'pause' : 'play'} />
                  </Button>
                  <Button icon title='删除模型' onClick={() => onDelete(model)}>
                    <Icon name='trash alternate outline' />
                  </Button>
                </Button.Group>
              </Table.Cell>
            </Table.Row>
          ))}
          {models.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan='10'>
                <div className='model-empty'>没有匹配的模型</div>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>
    </div>
  );
};

export default ModelTable;
